import { Button, Group, Loader, Stack, Text, Textarea } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { DeclarationStatus } from '@e-origin/shared';

import { useAdminRetrieveBatchLRNs, useChangeStatusDeclarations } from '@admin/hooks';
import { Select } from '@components';

interface ChangeStatusDeclarationsProps {
  onClose: () => void;
  batchCounter: number;
}

const changeStatusSchema = z.object({
  lrns: z.string().min(1, { message: 'LRNs are required' }),
  status: z.string().min(1, { message: 'Status is required' }),
});

export const ChangeStatusDeclarations: React.FC<ChangeStatusDeclarationsProps> = ({ onClose, batchCounter }) => {
  const changeStatusDeclarationsMutation = useChangeStatusDeclarations();
  const { isLoading: isLoadingLRNs, refetch: fetchBatchLRNs } = useAdminRetrieveBatchLRNs(batchCounter);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      lrns: '',
      status: '',
    },
    validate: zodResolver(changeStatusSchema),
    validateInputOnBlur: true,
  });

  const statusOptions = Object.keys(DeclarationStatus).map((key) => ({
    value: key,
    label: DeclarationStatus[key],
  }));

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);
    try {
      await changeStatusDeclarationsMutation.mutateAsync({
        lrns: values.lrns,
        status: values.status,
        batchCounter,
      });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleRetrieveLRNs = async () => {
    setFormError(null);
    const result = await fetchBatchLRNs();

    if (result.error) {
      setFormError((result.error as any)?.response?.data?.message || 'Failed to retrieve LRNs');
      return;
    }
    form.setFieldValue('lrns', result.data.join(',\n'));
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="lg" fw={500}>
            LRN list
          </Text>
          <Button onClick={handleRetrieveLRNs} disabled={isLoadingLRNs} size="sm">
            Retrieve Batch LRNs
          </Button>
        </Group>
        <Textarea {...form.getInputProps('lrns')} minRows={7} autosize maxRows={7} />
        <Select label="Status" placeholder="Select status" data={statusOptions} {...form.getInputProps('status')} searchable />
        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} disabled={changeStatusDeclarationsMutation.isPending} variant="transparent">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={changeStatusDeclarationsMutation.isPending || !form.isValid()}
            leftSection={changeStatusDeclarationsMutation.isPending && <Loader color="white" size="xs" />}
          >
            Change Status
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
